import type { FC } from 'react';

import { Button, IconButton, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import { useGetBrokerProfile } from 'src/api/broker-profile';
import { useGetUserInfo } from 'src/api/user';
import Iconify from 'src/components/iconify';
import { useAuth } from 'src/hooks/use-auth';
import { useBoolean } from 'src/hooks/use-boolean';
import { paths } from 'src/routes/paths';
import BrokerProfileEditDialog from 'src/sections/profile/broker-profile-edit-dialog';
import ProfileQuickEditForm from 'src/sections/profile/profile-edit-view';
import { calculateDays } from 'src/utils/format-time';

interface AccountPopoverProps {
  anchorEl: Element | null;
  onClose?: () => void;
  open?: boolean;
}

export const AccountPopover: FC<AccountPopoverProps> = props => {
  const confirm = useBoolean();
  const brokerEdit = useBoolean();

  const { anchorEl, onClose, open, ...other } = props;
  const router = useNavigate();
  const queryClient = useQueryClient();

  const { data: userInfoData } = useGetUserInfo({ enabled: !!open });

  const isBroker = useMemo(
    () =>
      (userInfoData?.data.type_name ?? userInfoData?.data.current_type_name) ===
      'Broker',
    [userInfoData?.data.current_type_name, userInfoData?.data.type_name],
  );

  const { data: brokerProfileRes } = useGetBrokerProfile({
    enabled: !!open && isBroker,
  });
  const brokerProfile = brokerProfileRes?.data;

  const { signOut } = useAuth();

  const handleLogout = useCallback(() => {
    try {
      signOut();

      queryClient.clear();
      onClose?.();
      void router(0);
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong!');
    }
  }, [signOut, queryClient, onClose, router]);

  const displayFirstLast = isBroker
    ? brokerProfile
      ? `${brokerProfile.firstname} ${brokerProfile.lastname}`.trim()
      : userInfoData?.data
        ? `${userInfoData.data.firstname ?? ''} ${userInfoData.data.lastname ?? ''}`.trim() ||
          '—'
        : '—'
    : userInfoData?.data
      ? `${userInfoData.data.firstname ?? ''} ${userInfoData.data.lastname ?? ''}`.trim() ||
        '—'
      : '—';

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'center',
        vertical: 'bottom',
      }}
      disableScrollLock
      onClose={onClose}
      open={!!open}
      PaperProps={{ sx: { width: 280 } }}
      {...other}
    >
      <Box sx={{ p: 2 }}>
        <Stack alignItems="center" display="flex" flexDirection="row">
          <Typography variant="body1" color="green" sx={{ flex: 1, minWidth: 0 }}>
            {displayFirstLast}
          </Typography>
          <IconButton
            size="small"
            aria-label={isBroker ? 'Edit broker profile' : 'Edit profile'}
            onClick={() =>
              isBroker ? brokerEdit.onTrue() : confirm.onTrue()
            }
          >
            <Iconify icon="tabler:user-edit" width={20} />
          </IconButton>
        </Stack>
        {isBroker && brokerProfile?.title ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {brokerProfile.title}
          </Typography>
        ) : null}

        {userInfoData?.data.type !== 8 && (
          <>
            <Typography variant="body2">
              {userInfoData?.data.current_group_name}
            </Typography>
          </>
        )}
      </Box>

      {userInfoData?.data.type !== 8 && <Divider sx={{ my: '0 !important' }} />}
      <Box sx={{ p: 2 }}>
        {userInfoData?.data.current_plan && (
          <Typography variant="body2">
            Plan: {userInfoData?.data.current_plan?.title}
          </Typography>
        )}
        {userInfoData?.data.current_plan && (
          <Typography variant="body2">
            Expire Plan:{' '}
            {calculateDays(userInfoData.data.current_plan.expired) > 0
              ? `${calculateDays(userInfoData.data.current_plan.expired)} Days`
              : 'Expired'}
          </Typography>
        )}
        {userInfoData?.data.type !== 8 && (
          <Button
            size="small"
            sx={{ mt: 2 }}
            variant="outlined"
            onClick={() => {
              onClose?.();
              router(paths.dashboard.subscriptionPlan.buy);
            }}
          >
            Plan Setting
          </Button>
        )}
      </Box>

      <Divider sx={{ my: '0 !important' }} />

      <Box
        sx={{
          display: 'flex',
          p: 1,
          justifyContent: 'center',
        }}
      >
        <Button size="small" color="error" onClick={handleLogout}>
          Logout
        </Button>
      </Box>
      <ProfileQuickEditForm
        onClose={confirm.onFalse}
        onSuccess={() => confirm.onFalse()}
        open={confirm.value}
      />
      <BrokerProfileEditDialog
        open={brokerEdit.value}
        onClose={brokerEdit.onFalse}
        onSuccess={() => brokerEdit.onFalse()}
      />
    </Popover>
  );
};
